/**
 * Navigate the TOP-LEVEL window out of the Shopify admin iframe.
 *
 * Why this exists at all: per Shopify's own docs, "because embedded apps are
 * rendered in the Shopify admin inside an iframe, they don't have permission to
 * manipulate the parent browser window, including redirects." A plain
 * `window.location = url` inside the frame would try to load admin.shopify.com
 * INSIDE our frame and fail.
 *
 * This matters for exactly one thing today: sending a merchant to Shopify's
 * hosted plan selection page. That page is the compliant billing path under
 * requirement 1.2.1, so if the redirect silently does nothing the app is back to
 * having no in-app way to pay (apparelhub-shopify-app#12).
 *
 * Three attempts, most correct first:
 *   1. App Bridge v4 (`window.open` with '_top'). App Bridge intercepts this and
 *      performs the admin navigation properly. This app is on App Bridge v4 --
 *      see utils/fetch.js using `window.shopify.idToken()`.
 *   2. Native `window.open(url, '_top')`, which is what a non-embedded load
 *      (or a browser that did not get App Bridge) needs.
 *   3. `window.top.location` as a last resort.
 *
 * Anchors should ALSO carry `target="_top"` so keyboard and middle-click work
 * without going through this function at all.
 */
export function redirectToTopLevel(url) {
  if (!url) return false;

  // 1 + 2: App Bridge v4 patches window.open, so this is both the App Bridge
  // path and the native fallback. Kept as one call deliberately: branching on
  // `window.shopify` would skip the patched implementation in the embedded case
  // if App Bridge had not finished booting.
  try {
    const opened = window.open(url, '_top');
    if (opened !== null) return true;
  } catch (err) {
    // Fall through. A blocked popup or a cross-origin guard lands here.
  }

  // 3: last resort.
  try {
    if (window.top) {
      window.top.location.href = url;
      return true;
    }
  } catch (err) {
    // Cross-origin access to window.top can throw; nothing left to try.
  }

  return false;
}

export default redirectToTopLevel;
